import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { doc } from 'firebase/firestore'
import { useDocumentData } from '@kage1020/react-firebase-hooks/firestore'
import { db } from '../firebase'
import { useAuth } from './AuthContext'
import { useEventTypes } from './EventTypesContext'
import { useEventDraft } from '../hooks/useEventDraft'
import { DEFAULT_TYPE } from '../constants/eventTypes'
import { displayWebappUrl } from '../utils/webappUrl'
import { useTranslation } from '../i18n'

const EventWorkspaceContext = createContext(null)

// eslint-disable-next-line react-refresh/only-export-components
export function useEventWorkspace() {
  const ctx = useContext(EventWorkspaceContext)
  if (!ctx) throw new Error('useEventWorkspace must be used inside <EventWorkspaceProvider>')
  return ctx
}

// Who may do what on this event, from its type and the person's role — the
// same rules the old single event page applied. Shared with the create form.
// eslint-disable-next-line react-refresh/only-export-components
export function eventPermissions({ typeConfig, isSysAdmin, eventRole }) {
  const typeAdminRole = typeConfig?.adminRole || 'eventadmin'
  return {
    typeAdminRole,
    isHidden: (id) => (typeConfig?.hiddenSections || []).includes(id),
    canPrizes: typeConfig?.capabilities?.prizes !== false,
    canEdit: isSysAdmin || eventRole === 'eventadmin' || eventRole === typeAdminRole,
    canSeeVideos: isSysAdmin || eventRole === 'eventadmin' || eventRole === typeAdminRole,
    isEventAdmin: isSysAdmin || eventRole === 'eventadmin',
  }
}

/**
 * One event's workspace: the live document, what this person may do with it,
 * and the draft its settings tabs edit.
 *
 * Editing is per tab: at most one tab is in edit mode (`editingSection`), and
 * its changes are kept here, above the routes, so the sidebar can tell when a
 * tab is about to be left with unsaved changes — and so the changes survive a
 * navigation the sidebar could not intercept (Back, a header link). See
 * EventWorkspace.jsx for the leave dialog.
 */
export function EventWorkspaceProvider({ eventId, children }) {
  const { isSysAdmin, eventRoles } = useAuth()
  const { getTypeConfig } = useEventTypes()
  const { lang } = useTranslation()

  const [event, loading, error] = useDocumentData(doc(db, 'events', eventId))
  const eventTypeId = event?.eventType || DEFAULT_TYPE
  const typeConfig = getTypeConfig(eventTypeId)
  const eventRole = eventRoles.find((r) => r.eventid === eventId)?.role ?? null
  const permissions = useMemo(
    () => eventPermissions({ typeConfig, isSysAdmin, eventRole }),
    [typeConfig, isSysAdmin, eventRole],
  )

  const draft = useEventDraft({ eventId, stored: event, lang })
  const [editingSection, setEditingSection] = useState(null)

  const startEdit = useCallback((sectionId) => setEditingSection(sectionId), [])

  const discardEdit = useCallback(() => {
    draft.discard()
    setEditingSection(null)
  }, [draft])

  // Throws, so the caller can report the failure and stay in edit mode.
  const saveEdit = useCallback(async () => {
    await draft.save()
    setEditingSection(null)
  }, [draft])

  const value = useMemo(() => ({
    eventId,
    event,
    loading,
    error,
    eventTypeId,
    typeConfig,
    eventRole,
    isSysAdmin,
    ...permissions,
    webappUrl: displayWebappUrl({ storedUrl: event?.webappUrl, typeConfig, eventType: eventTypeId, eventId }),
    draft,
    editingSection,
    // Unsaved changes exist only while a tab is being edited.
    unsaved: editingSection !== null && draft.dirty,
    startEdit,
    saveEdit,
    discardEdit,
  }), [eventId, event, loading, error, eventTypeId, typeConfig, eventRole, isSysAdmin, permissions, draft, editingSection, startEdit, saveEdit, discardEdit])

  return <EventWorkspaceContext.Provider value={value}>{children}</EventWorkspaceContext.Provider>
}

// The `s` every section component reads, for one tab.
// eslint-disable-next-line react-refresh/only-export-components
export function sectionStateFor(ws, sectionId) {
  return {
    eventId: ws.eventId,
    event: ws.event,
    eventTypeId: ws.eventTypeId,
    typeConfig: ws.typeConfig,
    typeAdminRole: ws.typeAdminRole,
    eventRole: ws.eventRole,
    isSysAdmin: ws.isSysAdmin,
    isHidden: ws.isHidden,
    canEdit: ws.canEdit,
    webappUrl: ws.webappUrl,
    editing: ws.editingSection === sectionId,
    draft: ws.draft.draft,
    setDraft: ws.draft.setDraft,
    setField: ws.draft.setField,
    setFile: ws.draft.setFile,
    info: ws.draft.info,
  }
}
